import React, { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { jobsAPI } from '../../services/api';
import JobForm from './JobForm';
import '../admin/AdminComponents.css';

const JobsManagement = () => {
  const { hasPermission } = useUser();
  const canCreateJobs = hasPermission('jobs.create');
  const canEditJobs = hasPermission('jobs.edit');
  const canDeleteJobs = hasPermission('jobs.delete');
  const canArchiveJobs = hasPermission('jobs.archive');
  const canViewApplications = hasPermission('applications.view');
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [applicationCounts, setApplicationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingJob, setEditingJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState({ name: '', displayOrder: 0 });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryEdit, setCategoryEdit] = useState({ name: '', displayOrder: 0 });

  useEffect(() => {
    loadData();
  }, [includeInactive]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [jobsResponse, categoriesResponse] = await Promise.all([
        jobsAPI.getAll(includeInactive),
        jobsAPI.getCategories(true)
      ]);
      const loadedJobs = jobsResponse.success ? jobsResponse.data : [];
      setJobs(loadedJobs);
      setCategories(categoriesResponse.success ? categoriesResponse.data : []);

      if (canViewApplications) {
        const countEntries = await Promise.all(loadedJobs.map(async job => {
          try {
            const response = await jobsAPI.getApplications(job.id, null);
            return [job.id, response.success ? response.data.length : 0];
          } catch {
            return [job.id, 0];
          }
        }));
        setApplicationCounts(Object.fromEntries(countEntries));
      } else {
        setApplicationCounts({});
      }
    } catch (error) {
      alert(error.message || 'Failed to load careers data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (job) => {
    const confirmed = window.confirm(
      `Permanently delete "${job.title}"?\n\nThis will also delete every application and uploaded resume attached to this job. This cannot be undone. Use Archive instead if you may need the job later.`
    );
    if (!confirmed) return;
    try {
      const response = await jobsAPI.delete(job.id);
      if (response.data?.deletedApplications > 0) {
        alert(`Job deleted with ${response.data.deletedApplications} associated application(s).`);
      }
      loadData();
    } catch (error) {
      alert(error.message || 'Failed to delete job position');
    }
  };

  const toggleActive = async (job) => {
    try {
      await jobsAPI.update(job.id, { active: !job.active });
      loadData();
    } catch (error) {
      alert(error.message || 'Failed to update job position');
    }
  };

  const createCategory = async (event) => {
    event.preventDefault();
    if (!categoryDraft.name.trim()) return;
    try {
      await jobsAPI.createCategory({
        name: categoryDraft.name.trim(),
        displayOrder: Number(categoryDraft.displayOrder) || 0,
        active: true
      });
      setCategoryDraft({ name: '', displayOrder: 0 });
      loadData();
    } catch (error) {
      alert(error.message || 'Failed to create category');
    }
  };

  const saveCategory = async (category) => {
    try {
      await jobsAPI.updateCategory(category.id, {
        name: categoryEdit.name.trim(),
        displayOrder: Number(categoryEdit.displayOrder) || 0
      });
      setEditingCategoryId(null);
      loadData();
    } catch (error) {
      alert(error.message || 'Failed to update category');
    }
  };

  const toggleCategory = async (category) => {
    try {
      await jobsAPI.updateCategory(category.id, { active: !category.active });
      loadData();
    } catch (error) {
      alert(error.message || 'Failed to update category');
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Delete the "${category.name}" filter category?`)) return;
    try {
      await jobsAPI.deleteCategory(category.id);
      loadData();
    } catch (error) {
      alert(error.message || 'Failed to delete category');
    }
  };

  if (loading) {
    return <div className="admin-section"><p>Loading careers...</p></div>;
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Careers Management</h2>
        <div className="admin-section-actions">
          <label className="admin-checkbox-label" style={{ marginRight: '1rem' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Show Inactive
          </label>
          {canCreateJobs && (
            <button
              className="admin-button primary"
              onClick={() => {
                setEditingJob(null);
                setShowForm(true);
              }}
            >
              Add Job Position
            </button>
          )}
        </div>
      </div>

      <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Filter Categories</h3>
      {canCreateJobs && (
        <form onSubmit={createCategory} className="admin-form-row" style={{ marginBottom: '1rem' }}>
          <input
            className="admin-form-input"
            value={categoryDraft.name}
            onChange={(event) => setCategoryDraft(previous => ({ ...previous, name: event.target.value }))}
            placeholder="Category name (e.g., Creative)"
            aria-label="Category name"
          />
          <input
            className="admin-form-input"
            type="number"
            value={categoryDraft.displayOrder}
            onChange={(event) => setCategoryDraft(previous => ({ ...previous, displayOrder: event.target.value }))}
            aria-label="Category display order"
            title="Display order"
            style={{ maxWidth: '130px' }}
          />
          <button className="admin-button primary" type="submit">Add Category</button>
        </form>
      )}
      <div className="admin-table-wrapper" style={{ marginBottom: '2rem' }}>
        <table className="admin-table">
          <thead>
            <tr><th>Order</th><th>Category</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id}>
                <td>
                  {editingCategoryId === category.id ? (
                    <input
                      className="admin-form-input"
                      type="number"
                      value={categoryEdit.displayOrder}
                      onChange={(event) => setCategoryEdit(previous => ({ ...previous, displayOrder: event.target.value }))}
                      style={{ maxWidth: '90px' }}
                    />
                  ) : category.display_order}
                </td>
                <td>
                  {editingCategoryId === category.id ? (
                    <input
                      className="admin-form-input"
                      value={categoryEdit.name}
                      onChange={(event) => setCategoryEdit(previous => ({ ...previous, name: event.target.value }))}
                    />
                  ) : category.name}
                </td>
                <td>
                  <button
                    className={`admin-button ${category.active ? 'success' : 'secondary'}`}
                    onClick={() => toggleCategory(category)}
                    disabled={!canEditJobs}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    {category.active ? 'Active' : 'Hidden'}
                  </button>
                </td>
                <td>
                  <div className="admin-table-actions">
                    {canEditJobs && (editingCategoryId === category.id ? (
                      <>
                        <button className="admin-button primary" onClick={() => saveCategory(category)}>Save</button>
                        <button className="admin-button secondary" onClick={() => setEditingCategoryId(null)}>Cancel</button>
                      </>
                    ) : (
                      <button
                        className="admin-icon-button"
                        onClick={() => {
                          setEditingCategoryId(category.id);
                          setCategoryEdit({ name: category.name, displayOrder: category.display_order });
                        }}
                        title="Edit category"
                      >
                        Edit
                      </button>
                    ))}
                    {canDeleteJobs && (
                      <button className="admin-icon-button danger" onClick={() => deleteCategory(category)} title="Delete category">
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '0 0 0.75rem' }}>Job Positions</h3>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order</th><th>Title</th><th>Category</th><th>Type</th><th>Openings</th>
              <th>Active</th><th>Applications</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>No job positions found</td></tr>
            ) : jobs.map(job => (
              <tr key={job.id}>
                <td>{job.display_order}</td>
                <td>{job.title}</td>
                <td>{job.category_name || 'Uncategorised'}</td>
                <td>{job.employment_type || 'Full-time'}</td>
                <td>{job.openings || 1}</td>
                <td>
                  {canArchiveJobs ? (
                    <button
                      className={`admin-button ${job.active ? 'success' : 'secondary'}`}
                      style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                      onClick={() => toggleActive(job)}
                    >
                      {job.active ? 'Active' : 'Archived'}
                    </button>
                  ) : <span>{job.active ? 'Active' : 'Archived'}</span>}
                </td>
                <td>{canViewApplications ? (applicationCounts[job.id] || 0) : '-'}</td>
                <td>
                  <div className="admin-table-actions">
                    {canEditJobs && (
                      <button
                        className="admin-icon-button"
                        onClick={() => {
                          setEditingJob(job);
                          setShowForm(true);
                        }}
                        title="Edit"
                      >
                        Edit
                      </button>
                    )}
                    {canDeleteJobs && (
                      <button className="admin-icon-button danger" onClick={() => handleDelete(job)} title="Permanently delete">
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <JobForm
          job={editingJob}
          onClose={() => {
            setShowForm(false);
            setEditingJob(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default JobsManagement;
